import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
    ComposedChart, AreaChart, Area
} from "recharts";
import {
    TrendingUp, TrendingDown, DollarSign, ShoppingBag, Package,
    Users, CreditCard, CheckCircle, Calculator,
    Building, Tag, Truck, FileText, Utensils, Coffee, Pizza,
    Cake, ShoppingCart, CalendarIcon, Target,
    BarChart as BarChartIcon, PieChart as PieChartIcon,
    AlertCircle, Shield, Activity, Scale, Wallet, ChevronDown,
    Download, Filter, Sparkles, Crown, Trophy, TrendingUp as TrendUp,
    RotateCcw, Star, Clock, PackageOpen, UserCheck,
    MessageSquare, ThumbsUp, Award, CreditCard as CreditCardIcon,
    Star as StarIcon, Percent, DollarSign as DollarSignIcon,
    Pizza as PizzaIcon, ShoppingBag as ShoppingBagIcon,
    Cake as CakeIcon, Coffee as CoffeeIcon
} from 'lucide-react';

const Revenue = ({ isMobile, colors, generateDateTrendData, generateOrderTypeData,
    generateCategoryData, generateTopItemsData, generatePaymentData,
    generateGrossNetData, generateDiscountImpactData, startDate, endDate }) => {


    // Summary Cards Component
    const SummaryCards = () => {
        const [summary, setSummary] = useState({
            totalRevenue: 0,
            totalOrders: 0,
            netRevenue: 0,
            grossRevenue: 0
        });
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const fetchSummary = async () => {
                const vendorId = localStorage.getItem("vendorId");
                const authToken = localStorage.getItem("authToken");

                setLoading(true);
                try {
                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: {
                                vendorId,
                                fromDate: startDate,
                                toDate: endDate
                            },
                            headers: {
                                Authorization: `Bearer ${authToken}`
                            }
                        }
                    );

                    const data = res.data || {};

                    setSummary({
                        totalRevenue: data.totalRevenue ?? 0,
                        totalOrders: data.totalOrders ?? 0,
                        netRevenue: data.netRevenue ?? 0,
                        grossRevenue: data.grossRevenue ?? data.totalRevenue ?? 0
                    });
                } catch (err) {
                    console.error("❌ Summary API error:", err);
                    setSummary({
                        totalRevenue: 0,
                        totalOrders: 0,
                        netRevenue: 0,
                        grossRevenue: 0
                    });
                } finally {
                    setLoading(false);
                }
            };

            fetchSummary();
        }, [startDate, endDate]);

        const totalDiscount = Math.max(
            summary.grossRevenue - summary.netRevenue,
            0
        );

        const avgOrderValue =
            summary.totalOrders > 0
                ? summary.totalRevenue / summary.totalOrders
                : 0;

        const cards = [
            {
                title: 'Revenue',
                value: `₹${summary.totalRevenue.toLocaleString('en-IN')}`,
                icon: <span style={{ fontSize: '18px', fontWeight: '700' }}>₹</span>,
                color: colors.success,
                bgColor: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
            },
            {
                title: 'Orders',
                value: summary.totalOrders.toLocaleString('en-IN'),
                icon: <ShoppingBag size={isMobile ? 16 : 18} />,
                color: colors.primary,
                bgColor: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
            },
            {
                title: 'Avg Order',
                value: `₹${Math.round(avgOrderValue).toLocaleString('en-IN')}`,
                icon: <Target size={isMobile ? 16 : 18} />,
                color: colors.accent,
                bgColor: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
            },
            {
                title: 'Discount',
                value: `₹${totalDiscount.toLocaleString('en-IN')}`,
                icon: <TrendingDown size={isMobile ? 16 : 18} />,
                color: colors.danger,
                bgColor: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
            }
        ];

        if (loading) {
            return (
                <div style={{ padding: 20, textAlign: "center", color: "#64748b" }}>
                    Loading summary...
                </div>
            );
        }

        return (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: isMobile ? '8px' : '12px',
                    marginBottom: isMobile ? '16px' : '20px',
                    marginTop: isMobile ?'-5px': '-28px'
                }}
            >
                {cards.map((card, index) => (
                    <div
                        key={index}
                        style={{
                            background: card.bgColor,
                            borderRadius: '12px',
                            padding: isMobile ? '12px' : '16px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                            border: '1px solid white',
                            position: 'relative'
                        }}
                    >
                        <p
                            style={{
                                fontSize: isMobile ? '10px' : '11px',
                                color: '#475569',
                                fontWeight: '600'
                            }}
                        >
                            {card.title}
                        </p>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <p
                                style={{
                                    fontSize: isMobile ? '16px' : '20px',
                                    fontWeight: '800',
                                    color: '#1e293b'
                                }}
                            >
                                {card.value}
                            </p>

                            <div
                                style={{
                                    width: isMobile ? '32px' : '40px',
                                    height: isMobile ? '32px' : '40px',
                                    borderRadius: '10px',
                                    background: 'rgba(255,255,255,0.6)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: card.color
                                }}
                            >
                                {card.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };


    const DateTrendChart = () => {
        const [apiData, setApiData] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const fetchData = async () => {
                const vendorId = localStorage.getItem("vendorId");
                const token = localStorage.getItem("authToken");

                setLoading(true);
                try {
                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: { vendorId, fromDate: startDate, toDate: endDate },
                            headers: { Authorization: `Bearer ${token}` }
                        }
                    );
                    setApiData(res.data);
                } catch (e) {
                    setApiData(null);
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }, [startDate, endDate]);

        const dateTrendData = useMemo(() => {
            if (!apiData?.dailyStats) return [];

            return apiData.dailyStats.map(d => ({
                date: d.date,
                revenue: d.revenue,
                netRevenue: d.revenue * 0.95
            }));
        }, [apiData]);

        if (loading) return <div>Loading...</div>;

        return (
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dateTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line dataKey="revenue" stroke={colors.primary} />
                    <Line dataKey="netRevenue" stroke={colors.success} />
                </LineChart>
            </ResponsiveContainer>
        );
    };



    // Order Type Chart
    const OrderTypeChart = ({
        isMobile,
        colors = {
            gradient2: "linear-gradient(135deg, #6366f1, #3b82f6)",
            primary: "#3b82f6"
        }
    }) => {

        /* -------------------- STATE -------------------- */

        const [generateOrderTypeData, setGenerateOrderTypeData] = useState([]);
        const [loading, setLoading] = useState(true);

        /* -------------------- API CALL -------------------- */

        useEffect(() => {
            const fetchOrderTypeStats = async () => {
                try {
                    const vendorId = localStorage.getItem("vendorId") || 1;
                    const token = localStorage.getItem("authToken");

                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: {
                                vendorId,
                                fromDate: "2025-12-17",
                                toDate: "2025-12-18"
                            },
                            headers: {
                                Authorization: token ? `Bearer ${token}` : undefined
                            }
                        }
                    );

                    const stats = res.data?.orderTypeRevenueStats || {};

                    /* -------------------- DATA TRANSFORM -------------------- */

                    const colorPalette = [
                        "#6366f1",
                        "#10b981",
                        "#f59e0b",
                        "#ef4444",
                        "#3b82f6"
                    ];

                    const formattedData = Object.keys(stats)
                        .filter(key => key !== "TOTAL")
                        .map((key, index) => ({
                            name: key.replace(/_/g, " "),
                            revenue: Number(stats[key].revenue || 0),
                            percentage: parseFloat(
                                String(stats[key].percentage).replace("%", "")
                            ),
                            color: colorPalette[index % colorPalette.length]
                        }));

                    setGenerateOrderTypeData(formattedData);

                } catch (err) {
                    console.error("❌ OrderType API Error");
                    console.error("Status:", err.response?.status);
                    console.error("Data:", err.response?.data);
                } finally {
                    setLoading(false);
                }
            };

            fetchOrderTypeStats();
        }, []);

        /* -------------------- STATES -------------------- */

        if (loading) {
            return <div style={{ padding: 40 }}>Loading order type chart...</div>;
        }

        if (!generateOrderTypeData.length) {
            return <div style={{ padding: 40 }}>No order type data available</div>;
        }

        /* -------------------- UI -------------------- */

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative'
            }}>

                {/* TOP BORDER */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: colors.gradient2
                }} />

                {/* HEADER */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: colors.gradient2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff'
                        }}>
                            <ShoppingBag size={24} />
                        </div>
                        <div>
                            <h3 style={{ fontSize: 22, fontWeight: 800 }}>
                                Order Type Distribution
                            </h3>
                            <p style={{ color: '#64748b' }}>
                                Revenue breakdown by order type
                            </p>
                        </div>
                    </div>

                    <div style={{
                        background: '#f8fafc',
                        padding: '8px 16px',
                        borderRadius: 12,
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center'
                    }}>
                        <BarChartIcon size={20} />
                        Revenue on Bars
                    </div>
                </div>

                {/* CHART */}
                <div style={{ height: isMobile ? 280 : 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={generateOrderTypeData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(v) => `₹${v.toLocaleString('en-IN')}`} />
                            <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                            <Legend />
                            <Bar dataKey="revenue" radius={[8, 8, 0, 0]}>
                                {generateOrderTypeData.map((entry, i) => (
                                    <Cell key={i} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* STATS */}
                <div style={{
                    marginTop: 20,
                    padding: 16,
                    background: '#f8fafc',
                    borderRadius: 16,
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>

                        <div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Total Revenue</div>
                            <div style={{ fontSize: 20, fontWeight: 800 }}>
                                ₹{generateOrderTypeData
                                    .reduce((s, i) => s + i.revenue, 0)
                                    .toLocaleString('en-IN')}
                            </div>
                        </div>

                        <div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Most Popular</div>
                            <div style={{ fontWeight: 700 }}>
                                {generateOrderTypeData
                                    .reduce((a, b) => a.revenue > b.revenue ? a : b).name}
                            </div>
                        </div>

                        <div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Top Share</div>
                            <div style={{ fontWeight: 700 }}>
                                {Math.max(...generateOrderTypeData.map(i => i.percentage))}%
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        );
    };

    // Category Chart
    const CategoryChart = () => {
        const [categoryData, setCategoryData] = useState([]);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const fetchCategoryData = async () => {
                const vendorId = localStorage.getItem("vendorId");
                const token = localStorage.getItem("authToken");

                setLoading(true);
                try {
                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: { vendorId, fromDate: startDate, toDate: endDate },
                            headers: { Authorization: `Bearer ${token}` }
                        }
                    );

                    const categories = res.data?.topSellingByCategory || [];
                    const totalRevenue = res.data?.totalRevenue || 0;

                    const totalQty = categories.reduce(
                        (sum, c) => sum + Number(c.quantity || 0),
                        0
                    );

                    const colorsList = [
                        "#6366f1",
                        "#10b981",
                        "#f59e0b",
                        "#ef4444",
                        "#8b5cf6",
                        "#06b6d4"
                    ];

                    const formatted = categories.map((c, index) => {
                        const ratio = totalQty > 0 ? c.quantity / totalQty : 0;
                        const revenue = Number((totalRevenue * ratio).toFixed(2));

                        return {
                            name: c.category,
                            revenue,
                            percentage: totalRevenue > 0
                                ? ((revenue / totalRevenue) * 100).toFixed(1)
                                : 0,
                            color: colorsList[index % colorsList.length]
                        };
                    });

                    setCategoryData(formatted);
                } catch (err) {
                    console.error("❌ Category API error", err);
                    setCategoryData([]);
                } finally {
                    setLoading(false);
                }
            };

            fetchCategoryData();
        }, [startDate, endDate]);

        if (loading) {
            return <div style={{ padding: 40 }}>Loading category chart...</div>;
        }

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)'
            }}>
                <h3 style={{ fontWeight: 800, marginBottom: 20 }}>
                    Category Performance
                </h3>

                <div style={{ height: isMobile ? 260 : 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={categoryData}
                                cx="50%"
                                cy="50%"
                                innerRadius={isMobile ? 50 : 80}
                                outerRadius={isMobile ? 90 : 120}
                                paddingAngle={2}
                                dataKey="revenue"
                                nameKey="name"
                                label={(e) =>
                                    `₹${e.revenue.toLocaleString("en-IN")} (${e.percentage}%)`
                                }
                            >
                                {categoryData.map((entry, index) => (
                                    <Cell
                                        key={index}
                                        fill={entry.color}
                                        stroke="white"
                                        strokeWidth={2}
                                    />
                                ))}
                            </Pie>

                            <Tooltip
                                formatter={(v) =>
                                    `₹${Number(v).toLocaleString("en-IN")}`
                                }
                            />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };


    // Category Performance
    const CategoryPerformance = () => {
        const [categoryData, setCategoryData] = useState([]);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const fetchCategoryPerformance = async () => {
                const vendorId = localStorage.getItem("vendorId");
                const token = localStorage.getItem("authToken");

                setLoading(true);
                try {
                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: { vendorId, fromDate: startDate, toDate: endDate },
                            headers: { Authorization: `Bearer ${token}` }
                        }
                    );

                    const categories = res.data?.topSellingByCategory || [];
                    const totalRevenue = res.data?.totalRevenue || 0;

                    const totalQty = categories.reduce(
                        (sum, c) => sum + Number(c.quantity || 0),
                        0
                    );

                    const formatted = categories.map(c => {
                        const revenue =
                            totalQty > 0
                                ? (totalRevenue * c.quantity) / totalQty
                                : 0;

                        return {
                            name: c.category,
                            revenue: Number(revenue.toFixed(2)),
                            percentage: totalRevenue > 0
                                ? ((revenue / totalRevenue) * 100).toFixed(1)
                                : 0
                        };
                    });

                    setCategoryData(formatted);
                } catch (err) {
                    console.error("❌ CategoryPerformance API error", err);
                    setCategoryData([]);
                } finally {
                    setLoading(false);
                }
            };

            fetchCategoryPerformance();
        }, [startDate, endDate]);

        if (loading) {
            return <div style={{ padding: 40 }}>Loading category performance...</div>;
        }

        if (categoryData.length === 0) {
            return <div style={{ padding: 40 }}>No category data available</div>;
        }

        const sorted = [...categoryData].sort((a, b) => b.revenue - a.revenue);
        const bestSelling = sorted[0];
        const worstSelling = sorted[sorted.length - 1];

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)'
            }}>
                <h3 style={{ fontWeight: 800, marginBottom: 24 }}>
                    Category Leaders
                </h3>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '24px'
                }}>
                    {/* ✅ BEST CATEGORY */}
                    <div style={{
                        background: '#ecfdf5',
                        borderRadius: '20px',
                        padding: '24px',
                        border: '2px solid #bbf7d0'
                    }}>
                        <p style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>
                            TOP PERFORMER
                        </p>

                        <h4 style={{ fontSize: 22, fontWeight: 800, marginTop: 8 }}>
                            {bestSelling.name}
                        </h4>

                        <p style={{ marginTop: 16, fontSize: 18 }}>
                            Revenue: <b>₹{bestSelling.revenue.toLocaleString("en-IN")}</b>
                        </p>

                        <p style={{ fontSize: 16, color: '#10b981', fontWeight: 700 }}>
                            {bestSelling.percentage}% Market Share
                        </p>
                    </div>

                    {/* ❌ WORST CATEGORY */}
                    <div style={{
                        background: '#fef2f2',
                        borderRadius: '20px',
                        padding: '24px',
                        border: '2px solid #fecaca'
                    }}>
                        <p style={{ fontSize: 12, fontWeight: 700, color: '#ef4444' }}>
                            NEEDS ATTENTION
                        </p>

                        <h4 style={{ fontSize: 22, fontWeight: 800, marginTop: 8 }}>
                            {worstSelling.name}
                        </h4>

                        <p style={{ marginTop: 16, fontSize: 18 }}>
                            Revenue: <b>₹{worstSelling.revenue.toLocaleString("en-IN")}</b>
                        </p>

                        <p style={{ fontSize: 16, color: '#ef4444', fontWeight: 700 }}>
                            {worstSelling.percentage}% Market Share
                        </p>
                    </div>
                </div>
            </div>
        );
    };


    // Item Table
    const ItemTable = () => {
        const [items, setItems] = useState([]);
        const [filter, setFilter] = useState("all");
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const fetchItems = async () => {
                const vendorId = localStorage.getItem("vendorId");
                const token = localStorage.getItem("authToken");

                setLoading(true);
                try {
                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: { vendorId, fromDate: startDate, toDate: endDate },
                            headers: { Authorization: `Bearer ${token}` }
                        }
                    );

                    const {
                        topSellingByCategory = [],
                        totalRevenue = 0,
                        netRevenue = 0,
                        totalOrders = 0
                    } = res.data || {};

                    const totalQty = topSellingByCategory.reduce(
                        (sum, i) => sum + Number(i.quantity || 0),
                        0
                    );

                    const formatted = topSellingByCategory.map((item, index) => {
                        const revenue =
                            totalQty > 0
                                ? (totalRevenue * item.quantity) / totalQty
                                : 0;

                        const orders =
                            totalQty > 0
                                ? Math.round((totalOrders * item.quantity) / totalQty)
                                : 0;

                        const profit =
                            totalRevenue > 0
                                ? (netRevenue / totalRevenue) * revenue
                                : 0;

                        const profitMargin =
                            revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : 0;

                        return {
                            name: item.item,
                            category: item.category,
                            orders,
                            revenue: Number(revenue.toFixed(2)),
                            profit: Number(profit.toFixed(2)),
                            profitMargin: Number(profitMargin)
                        };
                    });

                    setItems(formatted);
                } catch (err) {
                    console.error("❌ ItemTable API error", err);
                    setItems([]);
                } finally {
                    setLoading(false);
                }
            };

            fetchItems();
        }, [startDate, endDate]);

        const filteredItems =
            filter === "all"
                ? items
                : items.filter(i => i.category === filter);

        if (loading) {
            return <div style={{ padding: 40 }}>Loading top items...</div>;
        }

        return (
            <div
                style={{
                    background: "white",
                    borderRadius: "24px",
                    padding: isMobile ? "20px" : "28px",
                    marginBottom: "28px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.06)"
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: isMobile ? "flex-start" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: "12px",
                        marginBottom: "20px"
                    }}
                >
                    <div>
                        <h3
                            style={{
                                fontWeight: 800,
                                fontSize: isMobile ? "18px" : "22px",
                                color: "#1e293b",
                                marginBottom: "4px"
                            }}
                        >
                            Top Selling Items
                        </h3>
                        <p
                            style={{
                                fontSize: "14px",
                                color: "#64748b"
                            }}
                        >
                            Best performing menu items by revenue
                        </p>
                    </div>

                    {/* Filter */}
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{
                            padding: "10px 16px",
                            borderRadius: "12px",
                            border: "1px solid #e2e8f0",
                            background: "#f8fafc",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1e293b",
                            cursor: "pointer",
                            outline: "none",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                        }}
                    >
                        <option value="all">All Categories</option>
                        {[...new Set(items.map(i => i.category))].map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                {/* Table */}
                <div
                    style={{
                        overflowX: "auto",
                        borderRadius: "16px",
                        border: "1px solid #e2e8f0"
                    }}
                >
                    <table
                        style={{
                            width: "100%",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                            minWidth: "750px",
                            fontSize: "14px"
                        }}
                    >
                        <thead>
                            <tr
                                style={{
                                    background: "#f8fafc",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1
                                }}
                            >
                                {["Item", "Category", "Orders", "Revenue", "Profit", "Margin"].map(h => (
                                    <th
                                        key={h}
                                        style={{
                                            padding: "16px",
                                            textAlign: "left",
                                            fontSize: "12px",
                                            fontWeight: 700,
                                            color: "#475569",
                                            borderBottom: "2px solid #e2e8f0",
                                            letterSpacing: "0.4px",
                                            textTransform: "uppercase"
                                        }}
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {filteredItems.map((item, idx) => (
                                <tr
                                    key={idx}
                                    style={{
                                        background: idx % 2 === 0 ? "white" : "#f8fafc",
                                        transition: "background 0.2s ease"
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"}
                                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "white" : "#f8fafc"}
                                >
                                    <td style={{ padding: "16px", fontWeight: 600, color: "#1e293b" }}>
                                        {item.name}
                                    </td>

                                    <td style={{ padding: "16px" }}>
                                        <span
                                            style={{
                                                padding: "6px 12px",
                                                background: "#f1f5f9",
                                                borderRadius: "10px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#475569"
                                            }}
                                        >
                                            {item.category}
                                        </span>
                                    </td>

                                    <td style={{ padding: "16px", fontWeight: 700 }}>
                                        {item.orders}
                                    </td>

                                    <td style={{ padding: "16px", fontWeight: 700 }}>
                                        ₹{item.revenue.toLocaleString("en-IN")}
                                    </td>

                                    <td style={{ padding: "16px", fontWeight: 700 }}>
                                        ₹{item.profit.toLocaleString("en-IN")}
                                    </td>

                                    <td style={{ padding: "16px" }}>
                                        <span
                                            style={{
                                                padding: "6px 16px",
                                                borderRadius: "20px",
                                                fontWeight: 700,
                                                fontSize: "13px",
                                                color:
                                                    item.profitMargin > 40 ? "#10b981" :
                                                        item.profitMargin > 20 ? "#d97706" :
                                                            "#ef4444",
                                                background:
                                                    item.profitMargin > 40 ? "#dcfce7" :
                                                        item.profitMargin > 20 ? "#fef3c7" :
                                                            "#fee2e2",
                                                border:
                                                    item.profitMargin > 40 ? "1px solid #bbf7d0" :
                                                        item.profitMargin > 20 ? "1px solid #fde68a" :
                                                            "1px solid #fecaca"
                                            }}
                                        >
                                            {item.profitMargin}%
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );

    };





    // Payment Analysis
    const PaymentAnalysis = () => {
        const [paymentData, setPaymentData] = useState([]);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const fetchPaymentData = async () => {
                const vendorId = localStorage.getItem("vendorId");
                const token = localStorage.getItem("authToken");

                setLoading(true);
                try {
                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: { vendorId, fromDate: startDate, toDate: endDate },
                            headers: { Authorization: `Bearer ${token}` }
                        }
                    );

                    const paymentBreakdown = res.data?.paymentBreakdown || {};

                    const colorsList = [
                        "#7c3aed",
                        "#ec4899",
                        "#22c55e",
                        "#f59e0b"
                    ];

                    const formatted = Object.entries(paymentBreakdown).map(
                        ([key, value], index) => ({
                            name: key.replace("_", " "),
                            amount: Number(value),
                            color: colorsList[index % colorsList.length]
                        })
                    );

                    setPaymentData(formatted);
                } catch (err) {
                    console.error("❌ PaymentAnalysis API error", err);
                    setPaymentData([]);
                } finally {
                    setLoading(false);
                }
            };

            fetchPaymentData();
        }, [startDate, endDate]);

        if (loading) {
            return <div style={{ padding: 40 }}>Loading payment analysis...</div>;
        }

        if (!paymentData.length) {
            return <div style={{ padding: 40 }}>No payment data available</div>;
        }

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Top Accent */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #7c3aed, #ec4899)'
                }} />

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                    }}>
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <h3 style={{
                            fontSize: isMobile ? '18px' : '22px',
                            fontWeight: '800',
                            color: '#1e293b'
                        }}>
                            Payment Methods Analysis
                        </h3>
                        <p style={{ color: '#64748b' }}>
                            Revenue distribution by payment type
                        </p>
                    </div>
                </div>

                {/* Chart */}
                <div style={{ height: isMobile ? '280px' : '400px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={paymentData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />

                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                            />

                            <YAxis
                                tickFormatter={(v) => `₹${v.toLocaleString('en-IN')}`}
                                axisLine={false}
                                tickLine={false}
                            />

                            <Tooltip
                                formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, "Amount"]}
                            />

                            <Bar
                                dataKey="amount"
                                radius={[8, 8, 0, 0]}
                                label={{
                                    position: "top",
                                    formatter: (v) => `₹${v.toLocaleString("en-IN")}`,
                                    fontWeight: "bold"
                                }}
                            >
                                {paymentData.map((entry, index) => (
                                    <Cell key={index} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };


    // Gross Net Chart
    const GrossNetChart = () => {
        const [chartData, setChartData] = useState([]);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const fetchGrossNet = async () => {
                const vendorId = localStorage.getItem("vendorId");
                const token = localStorage.getItem("authToken");

                setLoading(true);
                try {
                    const res = await axios.get(
                        "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                        {
                            params: { vendorId, fromDate: startDate, toDate: endDate },
                            headers: { Authorization: `Bearer ${token}` }
                        }
                    );

                    const {
                        dailyStats = [],
                        totalRevenue = 0,
                        netRevenue = 0
                    } = res.data || {};

                    const formatted = dailyStats.map(d => {
                        const net =
                            totalRevenue > 0
                                ? (d.revenue / totalRevenue) * netRevenue
                                : 0;

                        return {
                            date: new Date(d.date).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short"
                            }),
                            gross: Number(d.revenue.toFixed(2)),
                            net: Number(net.toFixed(2))
                        };
                    });

                    setChartData(formatted);
                } catch (err) {
                    console.error("❌ GrossNetChart API error", err);
                    setChartData([]);
                } finally {
                    setLoading(false);
                }
            };

            fetchGrossNet();
        }, [startDate, endDate]);

        if (loading) {
            return <div style={{ padding: 40 }}>Loading gross vs net revenue...</div>;
        }

        if (!chartData.length) {
            return <div style={{ padding: 40 }}>No gross/net data available</div>;
        }

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Top Accent */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)'
                }} />

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                    }}>
                        <Scale size={24} />
                    </div>
                    <div>
                        <h3 style={{
                            fontSize: isMobile ? '18px' : '22px',
                            fontWeight: '800',
                            color: '#1e293b'
                        }}>
                            Gross vs Net Revenue
                        </h3>
                        <p style={{ color: '#64748b' }}>
                            Revenue before and after discounts
                        </p>
                    </div>
                </div>

                {/* Chart */}
                <div style={{ height: isMobile ? '240px' : '360px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />

                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                            />

                            <YAxis
                                tickFormatter={(v) => `₹${v.toLocaleString("en-IN")}`}
                                axisLine={false}
                                tickLine={false}
                            />

                            <Tooltip
                                formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`}
                            />

                            <Bar
                                dataKey="gross"
                                name="Gross Revenue"
                                fill={colors.primary}
                                radius={[8, 8, 0, 0]}
                            />

                            <Bar
                                dataKey="net"
                                name="Net Revenue"
                                fill={colors.success}
                                radius={[8, 8, 0, 0]}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };




    return (
        <>
            <SummaryCards />
            <DateTrendChart />
            <OrderTypeChart />
            <CategoryChart />
            <CategoryPerformance />
            <ItemTable />
            {/* <ItemPerformanceHighlights /> */}
            <PaymentAnalysis />
            <GrossNetChart />
            {/* <DiscountImpact /> */}
        </>
    );
};

export default Revenue;